<template>
	<a-modal
		class="g-video-row-edit-modal"
		:visible="visible"
		title="模块编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<div class="g-video-row-edit" @click="clearSelection">
				<div class="g-video-row-edit__actions" @click.stop>
					<button type="button" class="g-video-row-edit__batch-btn" @click="addBatch">添加一批</button>
					<button type="button" class="g-video-row-edit__batch-btn" @click="addOne">添加一格</button>
				</div>

				<div
					class="g-video-row-edit__card"
					:class="{ 'is-card': form.showCard }"
					:style="previewCardStyle"
				>
					<EditListSortGroup
						ref="sortGroup"
						class="g-video-row-edit__list"
						:style="{ gap: previewBlockStyle.itemGap }"
						@dragover="onSortDragOver"
					>
						<div
							v-for="(item, index) in form.items"
							:key="item.id"
							:data-sort-id="item.id"
							class="g-video-row-edit__row-wrap"
							:class="[
								{ 'is-active': selectedIndex === index },
								getEditListSortItemClass(dragId, item.id, dragOverId),
							]"
							:draggable="form.items.length > 1"
							@click.stop
							@dragstart="onItemDragStart(item.id)"
							@dragend="onItemDragEnd"
						>
						<div
							class="g-video-row-edit__row"
							:class="{ 'is-selected': selectedIndex === index }"
							@click="selectItem(index)"
						>
							<div
								class="g-video-row-edit__thumb"
								:style="previewThumbStyle"
							>
								<UploadProgressPanel
									v-if="isUploading && selectedIndex === index"
									:tasks="uploadTasks"
									variant="item"
									hide-filename
								/>
								<template v-else>
									<img v-if="getCoverUrl(item)" :src="getCoverUrl(item)" alt="" />
									<i v-else class="mbm-iconfont mbm-shipin"></i>
									<button
										type="button"
										class="g-video-row-edit__play"
										aria-label="播放"
										@click.stop="playEditMedia(getVideoUrl(item), 'video')"
									>
										<span class="g-video-row-edit__triangle"></span>
									</button>
								</template>
							</div>
							<div class="g-video-row-edit__info">
								<input
									v-model="item.title"
									type="text"
									class="g-video-row-edit__title-input"
									:style="{ color: item.titleColor }"
									maxlength="40"
									placeholder="标题"
									@click.stop="selectItem(index)"
									@focus="selectItem(index)"
								/>
								<input
									v-model="item.description"
									type="text"
									class="g-video-row-edit__desc-input"
									:style="{ color: item.descColor }"
									maxlength="80"
									placeholder="描述"
									@click.stop="selectItem(index)"
									@focus="selectItem(index)"
								/>
								<div
									class="g-video-row-edit__meta"
									:style="{ color: item.durationColor }"
								>
									<svg class="g-video-row-edit__clock" viewBox="0 0 24 24" fill="none" aria-hidden="true">
										<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
										<path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
									</svg>
									<span>{{ item.video?.durationText || '00:00' }}</span>
								</div>
							</div>
						</div>

						<div
							v-if="selectedIndex === index"
							class="g-video-row-edit__panel g-video-row-edit__panel--arrow g-video-row-edit__panel--float"
							:class="getPanelAlignClass(index)"
							@click.stop
						>
							<div class="g-video-row-edit__toolbar">
								<button type="button" class="g-video-row-edit__action" @click="triggerVideoSelect">视频上传</button>
								<button type="button" class="g-video-row-edit__action" @click="triggerCoverSelect">更换封面</button>
								<button
									type="button"
									class="g-video-row-edit__action"
									:class="{ 'is-active': !form.showCard }"
									@click="toggleShowCard"
								>{{ form.showCard ? '隐藏卡片' : '显示卡片' }}</button>
								<button
									v-if="form.showCard"
									type="button"
									class="g-video-row-edit__action"
									:class="{ 'is-active': activePanel === 'cardRadius' }"
									@click="togglePanel('cardRadius')"
								>卡片圆角</button>
								<button
									type="button"
									class="g-video-row-edit__action"
									:class="{ 'is-active': activePanel === 'coverRadius' }"
									@click="togglePanel('coverRadius')"
								>图片圆角</button>
								<button
									v-if="showMoveUpButton(index)"
									type="button"
									class="g-video-row-edit__action g-video-row-edit__action--icon"
									title="上移"
									@click="moveUp(index)"
								>
									<span>↑</span>
								</button>
								<button
									v-if="showMoveDownButton(index)"
									type="button"
									class="g-video-row-edit__action g-video-row-edit__action--icon"
									title="下移"
									@click="moveDown(index)"
								>
									<span>↓</span>
								</button>
								<button
									type="button"
									class="g-video-row-edit__action g-video-row-edit__action--danger-text"
									:disabled="form.items.length <= 1"
									@click="deleteItem(index)"
								>删除</button>
								<div class="g-video-row-edit__colors">
									<ColorPicker
										v-model="selectedItem.titleColor"
										title="标题颜色"
										@change="onTitleColorInput"
									/>
									<ColorPicker
										v-model="selectedItem.descColor"
										title="描述颜色"
										@change="onDescColorInput"
									/>
								</div>
							</div>
							<div v-if="activePanel === 'cardRadius'" class="g-video-row-edit__sub-row">
								<span class="g-video-row-edit__sub-label">卡片圆角 (px)</span>
								<a-input-number
									v-model:value="cardRadiusDraft"
									:min="0"
									:max="100"
									:precision="0"
									class="g-video-row-edit__sub-input"
								/>
								<button type="button" class="g-video-row-edit__sub-confirm" @click="applyCardRadius">确定</button>
							</div>
							<div v-if="activePanel === 'coverRadius'" class="g-video-row-edit__sub-row">
								<span class="g-video-row-edit__sub-label">图片圆角 (px)</span>
								<a-input-number
									v-model:value="coverRadiusDraft"
									:min="0"
									:max="100"
									:precision="0"
									class="g-video-row-edit__sub-input"
								/>
								<button type="button" class="g-video-row-edit__sub-confirm" @click="applyCoverRadius">确定</button>
							</div>
						</div>
					</div>
				</EditListSortGroup>
				</div>
			</div>
		</div>

		<input
			ref="coverInput"
			type="file"
			accept="image/*"
			class="g-video-row-edit__file-input"
			@change="onCoverSelect"
		/>
		<input
			ref="videoInput"
			type="file"
			accept="video/*,.mp4,.mov,.avi,.flv,.mkv,.webm,.m4v,.wmv"
			class="g-video-row-edit__file-input"
			@change="onVideoSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" class="g-video-row-edit-modal__ok" :disabled="isUploading" @click="onConfirm">确定</a-button>
		</template>

		<EditMediaPlayerOverlay
			:url="editPlayingUrl"
			:kind="editPlayingKind"
			@close="stopEditMediaPlay"
		/>
	</a-modal>
</template>

<script>
	import { getSwatchBorderColor, normalizeHexColor } from '../utils/richText.js';
	import ColorPicker from './ColorPicker.vue';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
	} from '../utils/localImage.js';
	import {
		filterValidLocalVideoList,
		isVideoFilename,
		MAX_LOCAL_VIDEO_SIZE,
	} from '../utils/localVideo.js';
	import {
		createDefaultVideoRowItem,
		createDefaultVideoRowItems,
		getVideoRowBlockStyle,
		getVideoRowCoverUrl,
		getVideoRowVideoUrl,
		normalizeVideoRowData,
		replaceVideoRowCover,
		replaceVideoRowVideo,
		revokeVideoRowItems,
		VIDEO_ROW_CARD_PADDING,
		VIDEO_ROW_DEFAULT_CARD_RADIUS,
		VIDEO_ROW_DEFAULT_COVER_RADIUS,
	} from '../utils/videoRow.js';
	import { ensureEditFloatPanelVisible, getGridPanelAlignClass } from '../utils/editPanelAlign.js';
	import editModalMediaPlay from '../mixins/editModalMediaPlay.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import EditMediaPlayerOverlay from './EditMediaPlayerOverlay.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';
	import {
		createEditListDragState,
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragStart,
		handleEditListSortDragOver,
		resolveSelectedIndexAfterListMove,
	} from '../utils/editListDragSort.js';

	export default {
		name: 'GVideoRowEditModal',
		components: { EditListSortGroup, EditMediaPlayerOverlay, UploadProgressPanel, ColorPicker },
		mixins: [editModalMediaPlay],
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizeVideoRowData({}),
				selectedIndex: null,
				activePanel: '',
				cardRadiusDraft: VIDEO_ROW_DEFAULT_CARD_RADIUS,
				coverRadiusDraft: VIDEO_ROW_DEFAULT_COVER_RADIUS,
				isUploading: false,
				uploadTasks: [],
				...createEditListDragState(),
			};
		},
		computed: {
			selectedItem() {
				return this.form.items[this.selectedIndex] || null;
			},
			previewBlockStyle() {
				return getVideoRowBlockStyle(this.form, { h5: false });
			},
			previewThumbStyle() {
				return {
					width: this.previewBlockStyle.thumbWidth,
					height: this.previewBlockStyle.thumbHeight,
					borderRadius: `${this.form.coverBorderRadius}px`,
				};
			},
			previewCardStyle() {
				if (!this.form.showCard) {
					return {};
				}
				return {
					borderRadius: `${this.form.cardBorderRadius}px`,
					padding: `${VIDEO_ROW_CARD_PADDING}px`,
				};
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.selectedIndex = null;
					this.activePanel = '';
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			getEditListSortItemClass,
			getPanelAlignClass(index) {
				return getGridPanelAlignClass(index, 1);
			},
			ensureActivePanelVisible() {
				ensureEditFloatPanelVisible(this, {
					wrapSelector: '.g-video-row-edit__row-wrap',
					panelSelector: '.g-video-row-edit__panel--float',
				});
			},
			normalizeColor(color) {
				return normalizeHexColor(color);
			},
			getCoverUrl(item) {
				return getVideoRowCoverUrl(item);
			},
			getVideoUrl(item) {
				return getVideoRowVideoUrl(item);
			},
			getSwatchStyle(color) {
				const normalized = normalizeHexColor(color);
				return {
					background: normalized,
					borderColor: getSwatchBorderColor(normalized),
				};
			},
			syncForm() {
				this.form = normalizeVideoRowData(this.data);
				this.cardRadiusDraft = this.form.cardBorderRadius;
				this.coverRadiusDraft = this.form.coverBorderRadius;
				if (
					this.selectedIndex != null
					&& this.selectedIndex >= this.form.items.length
				) {
					this.selectedIndex = null;
				}
			},
			selectItem(index) {
				this.selectedIndex = index;
				this.ensureActivePanelVisible();
			},
			clearSelection() {
				this.selectedIndex = null;
				this.activePanel = '';
			},
			togglePanel(panel) {
				this.activePanel = this.activePanel === panel ? '' : panel;
				if (panel === 'cardRadius') {
					this.cardRadiusDraft = this.form.cardBorderRadius;
				}
				if (panel === 'coverRadius') {
					this.coverRadiusDraft = this.form.coverBorderRadius;
				}
				if (this.activePanel) {
					this.ensureActivePanelVisible();
				}
			},
			toggleShowCard() {
				this.form.showCard = !this.form.showCard;
				if (!this.form.showCard && this.activePanel === 'cardRadius') {
					this.activePanel = '';
				}
			},
			showMoveUpButton(index) {
				return this.form.items.length > 1 && index > 0;
			},
			showMoveDownButton(index) {
				const len = this.form.items.length;
				return len > 1 && index < len - 1;
			},
			addBatch() {
				this.form.items.push(...createDefaultVideoRowItems(3));
				this.selectedIndex = this.form.items.length - 3;
			},
			addOne() {
				this.form.items.push(createDefaultVideoRowItem({}, this.form.items.length));
				this.selectedIndex = this.form.items.length - 1;
			},
			moveUp(index) {
				if (index <= 0) return;
				const items = [...this.form.items];
				[items[index - 1], items[index]] = [items[index], items[index - 1]];
				this.form.items = items;
				this.selectedIndex = index - 1;
			},
			moveDown(index) {
				if (index >= this.form.items.length - 1) return;
				const items = [...this.form.items];
				[items[index], items[index + 1]] = [items[index + 1], items[index]];
				this.form.items = items;
				this.selectedIndex = index + 1;
			},
			deleteItem(index) {
				if (this.form.items.length <= 1) return;
				const removed = this.form.items[index];
				if (removed) {
					revokeVideoRowItems([removed]);
				}
				this.form.items.splice(index, 1);
				if (this.selectedIndex === index) {
					this.selectedIndex = null;
					this.activePanel = '';
				} else if (this.selectedIndex != null && this.selectedIndex > index) {
					this.selectedIndex -= 1;
				}
			},
			onItemDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.sortGroup, this);
			},
			onItemDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.sortGroup, this.form.items, this, (result) => {
					this.selectedIndex = resolveSelectedIndexAfterListMove(
						this.selectedIndex,
						result.fromIndex,
						result.toIndex,
					);
				});
			},
			triggerCoverSelect() {
				this.$refs.coverInput?.click();
			},
			triggerVideoSelect() {
				this.$refs.videoInput?.click();
			},
			async onCoverSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file) || !this.selectedItem) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					this.selectedItem.cover = replaceVideoRowCover(
						this.selectedItem.cover,
						file,
						() => item,
					);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			async onVideoSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !this.selectedItem) return;
				if (!file.type?.startsWith('video/') && !isVideoFilename(file.name)) return;
				if (file.size > MAX_LOCAL_VIDEO_SIZE) return;
				const [validFile] = filterValidLocalVideoList([file], 1);
				if (!validFile) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([validFile]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'video',
						files: [validFile],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					this.selectedItem.video = replaceVideoRowVideo(this.selectedItem.video, item);
					this.selectedItem.cover = { id: '', filename: '', previewUrl: '', url: '', local: false };
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onTitleColorInput(color) {
				if (!this.selectedItem) return;
				this.selectedItem.titleColor = color;
			},
			onDescColorInput(color) {
				if (!this.selectedItem) return;
				this.selectedItem.descColor = color;
			},
			applyCardRadius() {
				this.form.cardBorderRadius = Math.max(0, Number(this.cardRadiusDraft) || 0);
				this.activePanel = '';
			},
			applyCoverRadius() {
				this.form.coverBorderRadius = Math.max(0, Number(this.coverRadiusDraft) || 0);
				this.activePanel = '';
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				const payload = normalizeVideoRowData({ ...this.form });
				this.$emit('confirm', payload);
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-video-row-edit__panel);
	.edit-float-toolbar(g-video-row-edit__toolbar);
	.edit-float-action(g-video-row-edit__action);

	.g-video-row-edit {
		min-height: 360px;
		padding: 4px 0 12px;
	}

	.g-video-row-edit__actions {
		display: flex;
		justify-content: flex-end;
		gap: 16px;
		margin-bottom: 16px;
	}

	.g-video-row-edit__batch-btn {
		border: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;

		&:hover {
			color: #1d4ed8;
		}
	}

	.g-video-row-edit__card {
		box-sizing: border-box;
		width: 100%;
		overflow: visible;

		&.is-card {
			background: #fff;
			box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
		}
	}

	.g-video-row-edit__list {
		display: flex;
		flex-direction: column;
		width: 100%;
		overflow: visible; 
	}

	.g-video-row-edit__row-wrap {
		position: relative;
		z-index: 0;
		overflow: visible;

		&.is-active {
			z-index: 2;
		}
	}

	.g-video-row-edit__row {
		display: flex;
		align-items: stretch;
		gap: 12px;
		box-sizing: border-box;
		border: 2px dashed #fff;
		border-radius: 2px;
		padding: 4px;
		cursor: pointer;

		&.is-selected {
			border-color: #285af9;
		}
	}

	.g-video-row-edit__thumb {
		position: relative;
		flex-shrink: 0;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 22px;
			color: #bfbfbf;
		}
	}

	.g-video-row-edit__play {
		position: absolute;
		top: 50%;
		left: 50%;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 30px;
		height: 30px;
		margin: -15px 0 0 -15px;
		padding: 0;
		border: none;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.45);
		cursor: pointer;
	}

	.g-video-row-edit__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 2px;
		border-top: 5px solid transparent;
		border-bottom: 5px solid transparent;
		border-left: 8px solid #fff;
	}

	.g-video-row-edit__info {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: 2px 0;
	}

	.g-video-row-edit__title-input,
	.g-video-row-edit__desc-input {
		display: block;
		width: 100%;
		border: none;
		background: transparent;
		outline: none;
		padding: 0;
		box-sizing: border-box;
	}

	.g-video-row-edit__title-input {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
	}

	.g-video-row-edit__desc-input {
		margin-top: 4px;
		font-size: 12px;
		line-height: 1.5;
	}

	.g-video-row-edit__meta {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-top: 6px;
		font-size: 11px;
		line-height: 1;
	}

	.g-video-row-edit__clock {
		width: 12px;
		height: 12px;
		flex-shrink: 0;
	}

	.g-video-row-edit__colors {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-left: 4px;
		flex-shrink: 0;
	}

	.g-video-row-edit__swatch {
		position: relative;
		width: 18px;
		height: 18px;
		border: 1px solid #d9d9d9;
		border-radius: 2px;
		cursor: pointer;
		flex-shrink: 0;
		overflow: hidden;
	}

	.g-video-row-edit__color-input {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		cursor: pointer;
		border: none;
		padding: 0;
	}

	.g-video-row-edit__sub-row {
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		gap: 8px;
		padding: 8px 12px 10px;
		border-top: 1px solid #f0f0f0;
		white-space: nowrap;
	}

	.g-video-row-edit__sub-label {
		font-size: 13px;
		color: #666;
		white-space: nowrap;
		flex-shrink: 0;
	}

	.g-video-row-edit__sub-input {
		width: 88px;
		flex-shrink: 0;
	}

	.g-video-row-edit__sub-confirm {
		flex-shrink: 0;
		height: 28px;
		padding: 0 12px;
		border: none;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 12px;
		line-height: 28px;
		white-space: nowrap;
		cursor: pointer;

		&:hover {
			background: #3868ff;
		}
	}

	.g-video-row-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-video-row-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();

		.g-video-row-edit-modal__ok {
			white-space: nowrap;
			flex-shrink: 0;

			&:hover,
			&:focus {
				background: #3868ff;
				border-color: #3868ff;
			}
		}
	}
</style>
